/* import __COLOCATED_TEMPLATE__ from './agent-trial-banner.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import type FinTrialService from 'embercom/services/fin-trial-service';
import type FinAgentTypeService from 'embercom/services/fin-agent-type-service';
import {
  AiAnswersState,
  type AgentAccessState,
  type AgentTrialType,
} from 'embercom/lib/operator/fin/types';
import { AGENT_OPTIONS } from 'embercom/lib/operator/resolution-bot/constants';
import RouteRegexes from 'embercom/lib/route-regexes';
import moment from 'moment-timezone';

const ALL_ROLES_ROUTE_REGEX = /^apps\.app\.automation\.fin-ai-agent\.all-roles(\.|$)/;

interface Signature {
  Args: {};
}

export default class AgentTrialBanner extends Component<Signature> {
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare finTrialService: FinTrialService;
  @service declare finAgentTypeService: FinAgentTypeService;

  @tracked showModal = false;

  private get isOnFinAiAgentRoute(): boolean {
    let name = this.router.currentRouteName;
    return name != null && RouteRegexes.automation.finAIAgentAll.test(name);
  }

  private get isOnAllRolesRoute(): boolean {
    let name = this.router.currentRouteName;
    return name != null && ALL_ROLES_ROUTE_REGEX.test(name);
  }

  private get role(): AgentTrialType | null {
    let current = this.finAgentTypeService.currentAgentType;
    if (current === 'support' || current === 'sales') {
      return current;
    }
    return null;
  }

  private get agentState(): AgentAccessState | undefined {
    return this.role ? this.finTrialService.getAgentTrialState(this.role) : undefined;
  }

  private get trialState(): AiAnswersState | undefined {
    return this.agentState?.state;
  }

  get isTrialNotStarted(): boolean {
    return this.trialState === AiAnswersState.trialNotStarted;
  }

  get isTrialStarted(): boolean {
    return this.trialState === AiAnswersState.trialStarted;
  }

  get isTrialEndingToday(): boolean {
    return this.isTrialStarted && this.daysRemaining === 0;
  }

  get displayBanner(): boolean {
    return (
      this.isOnFinAiAgentRoute &&
      !this.isOnAllRolesRoute &&
      (this.isTrialNotStarted || this.isTrialStarted)
    );
  }

  get daysRemaining(): number {
    let endDate = this.agentState?.trialDetails?.endDate;
    return endDate ? moment(endDate).diff(moment(), 'days') : 0;
  }

  get roleName(): string {
    return this.role === 'sales'
      ? this.intl.t('operator.fin.agent-trial-banner.role.sales')
      : this.intl.t('operator.fin.agent-trial-banner.role.support');
  }

  get selectedAgentValue(): string {
    return this.role === 'sales' ? AGENT_OPTIONS.FIN_SDR : AGENT_OPTIONS.FIN;
  }

  get modalAgentTypes(): AgentTrialType[] {
    return this.role ? [this.role] : [];
  }

  @action
  openModal() {
    this.showModal = true;
  }

  @action
  closeModal() {
    this.showModal = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'FinAiAgent::AgentTrialBanner': typeof AgentTrialBanner;
  }
}
